// src/TableMetadataModal.jsx
// FINAL CLEANED - COPY/PASTE READY
// ✅ Branch-aware
// ✅ Uses live getActiveBranch() from apiBase.js
// ✅ Hostess is read-only from logged-in user
// ✅ Staff routes use apiJSON() only
// ✅ Branch required for waiter/table checks
// ✅ Better sync with AuthContext + apiBase branch events

import React, { useState, useMemo, useEffect, useRef, useCallback } from "react";
import "./ModalStyles.css";

import { useAuth } from "./context/AuthContext";
import { API, setBranch, getActiveBranch, apiJSON } from "./apiBase";

/* -------------------- Branch labels -------------------- */
const BRANCH_LABEL = {
  jounieh: "Jounieh",
  batroun: "Batroun",
  tripoli: "Tripoli",
  zgharta: "Zgharta",
  ehden: "Ehden",
};

const CONCRETE_BRANCHES = ["jounieh", "batroun", "tripoli", "zgharta", "ehden"];

/* -------------------- local cache helpers -------------------- */
const loadJSON = (key, fallback) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
};

const saveJSON = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {}
};

const keyFor = (type, branch) => `bamboo:${type}:${branch || "global"}`;

/* -------------------- defaults (local fallback) -------------------- */
const DEFAULTS = {
  waiters: {
    jounieh: ["Moliere", "Mozart", "Hassan", "Hsein", "Ali"],
    batroun: ["Moliere", "Mozart", "Hassan", "Hsein", "Ali"],
    tripoli: ["Moliere", "Mozart", "Hassan", "Hsein", "Ali"],
    zgharta: ["Moliere", "Mozart", "Hassan", "Hsein", "Ali"],
    ehden: ["Moliere", "Mozart", "Hassan", "Hsein", "Ali"],
  },
  hostesses: {
    jounieh: ["Lea", "Yana"],
    batroun: ["Lea", "Yana"],
    tripoli: ["Lea", "Yana"],
    zgharta: ["Lea", "Yana"],
    ehden: ["Lea", "Yana"],
  },
};

function norm(v) {
  return String(v || "").trim().toLowerCase();
}

function isConcreteBranch(v) {
  return CONCRETE_BRANCHES.includes(norm(v));
}

/* -------------------- table exists check -------------------- */
async function checkTableExists(tableName) {
  if (!tableName) return false;

  try {
    const data = await apiJSON("/saved-tables", { method: "GET" });

    let list = [];
    if (Array.isArray(data)) list = data;
    else if (Array.isArray(data?.tables)) list = data.tables;
    else if (Array.isArray(data?.rows)) list = data.rows;

    const target = norm(tableName);

    return list.some((row) => {
      if (typeof row === "string") return norm(row) === target;

      const candidate =
        row?.table_name ||
        row?.table ||
        row?.name ||
        row?.id ||
        row?.tableNumber ||
        "";

      return norm(candidate) === target;
    });
  } catch (e) {
    console.warn("checkTableExists failed:", e);
    return false;
  }
}

/* -------------------- UI bits -------------------- */
function Chip({ label, onRemove }) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "4px 10px",
        margin: "4px 6px 0 0",
        borderRadius: 999,
        background: "var(--panel, #f2f2f2)",
        border: "1px solid rgba(0,0,0,.08)",
        fontSize: 12,
      }}
    >
      {label}
      <button
        type="button"
        onClick={onRemove}
        aria-label={`Remove ${label}`}
        style={{
          border: "none",
          background: "transparent",
          cursor: "pointer",
          fontSize: 14,
          lineHeight: 1,
          opacity: 0.8,
        }}
        title="Remove"
      >
        ✖
      </button>
    </span>
  );
}

function StaffManager({
  open,
  onClose,
  branch,
  hostesses,
  setHostesses,
  waiters,
  setWaiters,
  isAuthenticated,
}) {
  const [hInput, setHInput] = useState("");
  const [wInput, setWInput] = useState("");
  const [busy, setBusy] = useState(false);

  const hasBranch = isConcreteBranch(branch);
  const disabled = !isAuthenticated || !hasBranch || busy;

  const optimistic = useCallback(
    (setList, next, type) => {
      setList(next);
      saveJSON(keyFor(type, branch), next);
    },
    [branch, setHostesses, setWaiters]
  );

  const fetchStaff = useCallback(async () => {
    if (!hasBranch) return;

    setBusy(true);
    try {
      const data = await apiJSON("/staff", { method: "GET" });

      const h = Array.isArray(data?.hostesses) ? data.hostesses : [];
      const w = Array.isArray(data?.waiters) ? data.waiters : [];

      setHostesses(h);
      setWaiters(w);

      saveJSON(keyFor("hostesses", branch), h);
      saveJSON(keyFor("waiters", branch), w);
    } catch (e) {
      console.warn("Failed to fetch staff:", e);
    } finally {
      setBusy(false);
    }
  }, [branch, hasBranch, setHostesses, setWaiters]);

  const onAdd = async (role, name) => {
    const n = String(name || "").trim();
    if (!n || disabled) return;

    const type = role === "hostess" ? "hostesses" : "waiters";
    const list = role === "hostess" ? hostesses : waiters;
    const setList = role === "hostess" ? setHostesses : setWaiters;

    if (list.some((x) => norm(x) === norm(n))) return;

    const next = [...list, n].sort((a, b) => a.localeCompare(b));
    optimistic(setList, next, type);

    try {
      await apiJSON("/staff/add", {
        method: "POST",
        body: { role, name: n },
      });
    } catch (e) {
      optimistic(setList, [...list], type);
      alert(`Failed to add: ${n}\n${e?.message || e}`);
    }
  };

  const onRemove = async (role, name) => {
    if (disabled) return;

    const type = role === "hostess" ? "hostesses" : "waiters";
    const list = role === "hostess" ? hostesses : waiters;
    const setList = role === "hostess" ? setHostesses : setWaiters;

    const next = list.filter((x) => x !== name);
    optimistic(setList, next, type);

    try {
      await apiJSON("/staff/remove", {
        method: "DELETE",
        body: { role, name },
      });
    } catch (e) {
      optimistic(setList, [...list], type);
      alert(`Failed to remove: ${name}\n${e?.message || e}`);
    }
  };

  const doReset = async () => {
    if (disabled) return;

    try {
      const data = await apiJSON("/staff/reset-defaults", {
        method: "POST",
      });

      const nextH = Array.isArray(data?.hostesses) ? data.hostesses : [];
      const nextW = Array.isArray(data?.waiters) ? data.waiters : [];

      setHostesses(nextH);
      setWaiters(nextW);

      saveJSON(keyFor("hostesses", branch), nextH);
      saveJSON(keyFor("waiters", branch), nextW);
    } catch (e) {
      const defH = DEFAULTS.hostesses[branch] || [];
      const defW = DEFAULTS.waiters[branch] || [];

      setHostesses(defH);
      setWaiters(defW);

      saveJSON(keyFor("hostesses", branch), defH);
      saveJSON(keyFor("waiters", branch), defW);

      alert(`Reset failed.\n${e?.message || e}\nUsing local defaults.`);
    }
  };

  useEffect(() => {
    if (!open || !isAuthenticated || !hasBranch) return;
    fetchStaff();
  }, [open, isAuthenticated, hasBranch, fetchStaff]);

  if (!open) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content mobile-modal">
        <h3>
          👥 Manage Staff —{" "}
          <span style={{ textTransform: "capitalize" }}>
            {BRANCH_LABEL[branch] || branch || "—"}
          </span>
        </h3>

        {!isAuthenticated && (
          <div style={{ margin: "10px 0 14px", fontSize: 12, opacity: 0.85 }}>
            ⚠️ You must be logged in to manage staff.
          </div>
        )}

        {isAuthenticated && !hasBranch && (
          <div style={{ margin: "10px 0 14px", fontSize: 12, opacity: 0.85 }}>
            ⚠️ Select a branch first.
          </div>
        )}

        <div style={{ marginBottom: 16, opacity: disabled ? 0.6 : 1 }}>
          <label style={{ display: "block", marginBottom: 6 }}>Hostesses</label>

          <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
            <input
              type="text"
              placeholder="Add hostess name"
              value={hInput}
              onChange={(e) => setHInput(e.target.value)}
              onKeyDown={async (e) => {
                if (e.key === "Enter") {
                  await onAdd("hostess", hInput);
                  setHInput("");
                }
              }}
              style={{ flex: 1 }}
              disabled={disabled}
            />
            <button
              type="button"
              className="btn primary"
              onClick={async () => {
                await onAdd("hostess", hInput);
                setHInput("");
              }}
              disabled={disabled}
            >
              ➕ Add
            </button>
          </div>

          <div>
            {hostesses.map((h) => (
              <Chip key={h} label={h} onRemove={() => onRemove("hostess", h)} />
            ))}
          </div>
        </div>

        <div style={{ marginBottom: 16, opacity: disabled ? 0.6 : 1 }}>
          <label style={{ display: "block", marginBottom: 6 }}>Waiters</label>

          <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
            <input
              type="text"
              placeholder="Add waiter name"
              value={wInput}
              onChange={(e) => setWInput(e.target.value)}
              onKeyDown={async (e) => {
                if (e.key === "Enter") {
                  await onAdd("waiter", wInput);
                  setWInput("");
                }
              }}
              style={{ flex: 1 }}
              disabled={disabled}
            />
            <button
              type="button"
              className="btn primary"
              onClick={async () => {
                await onAdd("waiter", wInput);
                setWInput("");
              }}
              disabled={disabled}
            >
              ➕ Add
            </button>
          </div>

          <div>
            {waiters.map((w) => (
              <Chip key={w} label={w} onRemove={() => onRemove("waiter", w)} />
            ))}
          </div>
        </div>

        <div className="modal-buttons" style={{ justifyContent: "space-between" }}>
          <button type="button" className="btn" onClick={doReset} disabled={disabled}>
            ♻ Reset to Defaults
          </button>
          <button type="button" className="btn" onClick={onClose}>
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

/* -------------------- MAIN COMPONENT -------------------- */
const TableMetadataModal = ({ onSubmit, onClose }) => {
  const { user, isAuthenticated, fetchMe } = useAuth();

  const hostessName = useMemo(() => {
    const u = user || null;

    const full = String(u?.full_name || "").trim();
    if (full) return full;

    const dn = String(u?.display_name || "").trim();
    if (dn) return dn;

    const fn = String(u?.first_name || "").trim();
    const ln = String(u?.last_name || "").trim();
    const combined = `${fn} ${ln}`.trim();
    if (combined) return combined;

    return String(u?.sub || u?.email || u?.username || "").trim();
  }, [user]);

  const readBranch = useCallback(() => {
    return getActiveBranch() || "";
  }, []);

  const [branchLocal, setBranchLocal] = useState(() => readBranch());
  const branch = branchLocal;

  const [hostess, setHostess] = useState("");
  const [waiter, setWaiter] = useState("");
  const [tableNumber, setTableNumber] = useState("");

  const [me, setMe] = useState(null);

  const [hostesses, setHostesses] = useState(() =>
    loadJSON(keyFor("hostesses", readBranch()), DEFAULTS.hostesses[readBranch()] || [])
  );
  const [waiters, setWaiters] = useState(() =>
    loadJSON(keyFor("waiters", readBranch()), DEFAULTS.waiters[readBranch()] || [])
  );

  const [tableExists, setTableExists] = useState(false);
  const [checkingTable, setCheckingTable] = useState(false);
  const [tableMessage, setTableMessage] = useState("");
  const prevExistsRef = useRef(false);

  const [manageOpen, setManageOpen] = useState(false);

  useEffect(() => {
    const sync = () => {
      setBranchLocal(readBranch());
    };

    sync();
    window.addEventListener("bamboo:branch", sync);
    window.addEventListener("app:branch", sync);

    return () => {
      window.removeEventListener("bamboo:branch", sync);
      window.removeEventListener("app:branch", sync);
    };
  }, [readBranch]);

  useEffect(() => {
    setHostesses(loadJSON(keyFor("hostesses", branch), DEFAULTS.hostesses[branch] || []));
    setWaiters(loadJSON(keyFor("waiters", branch), DEFAULTS.waiters[branch] || []));
    setWaiter("");
    setTableExists(false);
    setCheckingTable(false);
    setTableMessage("");
    prevExistsRef.current = false;
  }, [branch]);

  useEffect(() => {
    if (user) setMe(user);
  }, [user]);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetchMe?.();
        const u = res?.user ? res.user : res;
        if (u) setMe(u);
      } catch {}
    })();
  }, [fetchMe]);

  useEffect(() => {
    setHostess(String(hostessName || "").trim());
  }, [hostessName]);

  const userBranches = useMemo(() => {
    return Array.isArray(me?.branches) ? me.branches : [];
  }, [me]);

  const concreteBranches = useMemo(() => {
    return userBranches
      .map((x) => norm(x))
      .filter((x) => x && x !== "*" && x !== "central" && isConcreteBranch(x));
  }, [userBranches]);

  const uniqueConcrete = useMemo(() => {
    return Array.from(new Set(concreteBranches));
  }, [concreteBranches]);

  const isWildcardUser = useMemo(() => userBranches.some((x) => norm(x) === "*"), [userBranches]);
  const isSingleBranchUser = uniqueConcrete.length === 1;
  const needsExplicitBranch = isWildcardUser || uniqueConcrete.length > 1;

  useEffect(() => {
    if (uniqueConcrete.length === 1) {
      const only = uniqueConcrete[0];
      if (only && only !== branch) {
        setBranch(only, { reload: false });
        setBranchLocal(only);
      }
    }
  }, [uniqueConcrete, branch]);

  useEffect(() => {
    const name = String(tableNumber || "").trim();

    if (!name) {
      setTableExists(false);
      setCheckingTable(false);
      setTableMessage("");
      prevExistsRef.current = false;
      return;
    }

    if (!isConcreteBranch(branch)) {
      setTableExists(false);
      setCheckingTable(false);
      setTableMessage("⚠️ Select a branch first.");
      prevExistsRef.current = false;
      return;
    }

    let cancelled = false;
    setCheckingTable(true);
    setTableMessage("");

    const handle = setTimeout(() => {
      (async () => {
        const exists = await checkTableExists(name);
        if (cancelled) return;

        setTableExists(exists);
        setCheckingTable(false);
        setTableMessage(
          exists
            ? "⚠️ A table with this number already exists in this branch."
            : "✅ Table number looks available."
        );
      })();
    }, 400);

    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [tableNumber, branch]);

  useEffect(() => {
    if (tableExists && !prevExistsRef.current && String(tableNumber || "").trim()) {
      alert(
        "⚠️ This table number is not available.\n\nA table with this number already exists in this branch.\nPlease change it, or open saved tables."
      );
    }
    prevExistsRef.current = tableExists;
  }, [tableExists, tableNumber]);

  const handleSubmit = () => {
    const trimmedTable = String(tableNumber || "").trim();

    if (!isAuthenticated || !hostess) {
      alert("⚠️ You must be logged in (hostess auto-fills from your account).");
      return;
    }

    if (!isConcreteBranch(branch)) {
      alert("⚠️ Please select a branch first.");
      return;
    }

    if (!String(waiter || "").trim() || !trimmedTable) {
      alert("⚠️ Please fill all fields");
      return;
    }

    if (tableExists) {
      alert("This table number is not available.\nPlease change it or open saved tables.");
      return;
    }

    onSubmit({
      hostess,
      waiter: String(waiter || "").trim(),
      table_number: trimmedTable,
      branch,
      api_base: API,
      x_branch: branch,
      user: me
        ? {
            full_name: me.full_name,
            first_name: me.first_name,
            last_name: me.last_name,
            display_name:
              me.display_name ||
              me.full_name ||
              `${me.first_name || ""} ${me.last_name || ""}`.trim(),
            email: me.sub || me.email,
            username: me.username || me.sub || me.email,
            branches: me.branches,
          }
        : null,
    });
  };

  const onPickBranchLocal = (e) => {
    const b = norm(e.target.value);
    setBranch(b, { reload: false });
    setBranchLocal(b);
  };

  return (
    <>
      <div className="modal-overlay">
        <div className="modal-content mobile-modal">
          <h2>📝 Table Info</h2>

          <label>Branch</label>
          <select
            value={branch || ""}
            onChange={onPickBranchLocal}
            disabled={isSingleBranchUser}
            title={
              isSingleBranchUser
                ? `Auto-selected from your login: ${BRANCH_LABEL[branch] || branch}`
                : "Select the target branch"
            }
          >
            <option value="" disabled>
              Select Branch
            </option>
            {CONCRETE_BRANCHES.map((b) => (
              <option key={b} value={b}>
                {BRANCH_LABEL[b]}
              </option>
            ))}
          </select>

          {me && (
            <div style={{ margin: "10px 0 10px", fontSize: 12, opacity: 0.9 }}>
              Logged in as:{" "}
              <b>
                {me.full_name ||
                  me.display_name ||
                  `${me.first_name || ""} ${me.last_name || ""}`.trim() ||
                  "—"}
              </b>{" "}
              <span style={{ opacity: 0.7 }}>
                ({me.sub || me.email || me.username || ""})
              </span>

              {needsExplicitBranch && (
                <div style={{ marginTop: 6, fontSize: 12, opacity: 0.85 }}>
                  ℹ️ Your account has multiple branches. Selecting branch here is required.
                </div>
              )}
            </div>
          )}

          <div style={{ display: "flex", justifyContent: "flex-end", margin: "6px 0 12px" }}>
            <button
              type="button"
              className="btn"
              onClick={() => setManageOpen(true)}
              disabled={!isAuthenticated || !isConcreteBranch(branch)}
              title={
                !isAuthenticated
                  ? "Login required"
                  : branch
                  ? `Manage staff for ${BRANCH_LABEL[branch]}`
                  : "Select a branch first"
              }
            >
              👥 Manage Staff {branch ? `(${BRANCH_LABEL[branch]})` : ""}
            </button>
          </div>

          <label>Hostess (Auto from your account)</label>
          <input
            type="text"
            value={hostess}
            readOnly
            placeholder="Login required"
            style={{ opacity: hostess ? 1 : 0.85 }}
          />

          <label>Waiter</label>
          <select
            value={waiter}
            onChange={(e) => setWaiter(e.target.value)}
            disabled={!isConcreteBranch(branch)}
          >
            <option value="">
              {isConcreteBranch(branch) ? "Select Waiter" : "Select branch first"}
            </option>
            {waiters.map((w) => (
              <option key={w} value={w}>
                {w}
              </option>
            ))}
          </select>

          <label>Table Number</label>
          <input
            type="text"
            value={tableNumber}
            onChange={(e) => setTableNumber(e.target.value)}
          />

          {tableNumber && (
            <div style={{ marginTop: 4, fontSize: 12 }}>
              {checkingTable ? (
                <span>⏳ Checking table number…</span>
              ) : tableMessage ? (
                <span
                  style={{
                    color: tableExists ? "#f97316" : "#22c55e",
                    fontWeight: tableExists ? 500 : 400,
                  }}
                >
                  {tableMessage}
                </span>
              ) : null}
            </div>
          )}

          <div style={{ marginTop: 10, fontSize: 12, opacity: 0.85 }}>
            Server: <code>{API || "—"}</code> • Branch: <code>{branch || "—"}</code>
          </div>

          <div className="modal-buttons">
            <button
              type="button"
              onClick={handleSubmit}
              className="btn primary"
              disabled={checkingTable || tableExists}
            >
              Save
            </button>
            <button type="button" onClick={onClose} className="btn danger">
              Cancel
            </button>
          </div>
        </div>
      </div>

      <StaffManager
        open={manageOpen}
        onClose={() => setManageOpen(false)}
        branch={branch}
        hostesses={hostesses}
        setHostesses={setHostesses}
        waiters={waiters}
        setWaiters={setWaiters}
        isAuthenticated={isAuthenticated}
      />
    </>
  );
};

export default TableMetadataModal;